import React, { useRef, useLayoutEffect } from 'react';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4charts from '@amcharts/amcharts4/charts';
import am4themes_animated from '@amcharts/amcharts4/themes/animated';
import { Box } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { colors } from '../../../../theme/color-palette';
import PropTypes from 'prop-types';
import { GRID_DISTANCE } from '../../../../constants/constant';
import { getToolTipFormat } from './utils.js';

const { FONT_COLOR, OFF_WHITE } = colors;

// chart styles
const useStyles = makeStyles(() => ({
	ComparisonChart: {
		height: (P) => P.chartHeight,
		width: (P) => P.chartWidth,
	},
}));

am4core.options.commercialLicense = true;

const ComparisonChart = (props) => {
	const {
		chartData,
		chartValue,
		xAxisData,
		chartWidth,
		chartHeight,
		chartName,
		weekFilter,
		yAxisMinValue,
	} = props;

	const chartRef = useRef(null);
	const classes = useStyles({
		...props,
		chartHeight,
		chartWidth,
	});

	useLayoutEffect(() => {
		am4core.useTheme(am4themes_animated);

		// creating lineChart Object
		let lineChart = am4core.create(chartName, am4charts.XYChart);
		lineChart.hiddenState.properties.opacity = 0;

		// setting the data for the line Chart
		lineChart.data = chartData?.details;

		// Create x axes
		let categoryAxis;
		categoryAxis = lineChart.xAxes.push(new am4charts.CategoryAxis());
		categoryAxis.dataFields.category = xAxisData.nonDate;

		const readingDataPointFormat = "'{categoryX}' '{valueY}'";

		//Label Formatting
		categoryAxis.renderer.minGridDistance = 0;
		categoryAxis.renderer.labels.template.fontSize = 12;
		categoryAxis.renderer.labels.template.fill = am4core.color(FONT_COLOR);
		categoryAxis.renderer.grid.template.location = 'none';
		categoryAxis.renderer.grid.template.disabled = true;

		// creating y axes
		const valueAxis = lineChart.yAxes.push(new am4charts.ValueAxis());
		valueAxis.renderer.opposite = false;
		valueAxis.renderer.labels.template.fontSize = 12;
		valueAxis.min = 4;
		valueAxis.renderer.labels.template.fill = am4core.color(FONT_COLOR);
		if (yAxisMinValue !== undefined) {
			valueAxis.min = yAxisMinValue;
		}

		// formatting y axis
		valueAxis.numberFormatter = new am4core.NumberFormatter();
		valueAxis.numberFormatter.numberFormat = '#.##%';

		// removed cursor Tooltip on the axis
		categoryAxis.cursorTooltipEnabled = false;
		valueAxis.cursorTooltipEnabled = false;

		let count = 0;

		/**
		 * Create series
		 * @param field - valueY on the chart
		 * @param name - name of the chart
		 * @param color - color of the line chart
		 * @param dasharray - style of the line chart
		 * @return series
		 */

		function createLineSeries(field, name, color, dasharray) {
			// creating line series
			const series = lineChart.series.push(new am4charts.LineSeries());
			series.itemReaderText = readingDataPointFormat;
			series.focusable = true;
			series.dataFields.valueY = field;

			series.dataFields.categoryX = xAxisData.nonDate;
			if (count === 0) {
				series.tooltipHTML = getToolTipFormat();
				series.tooltip.label.padding(0, 0, 0, 0);
				count++;
			}

			categoryAxis.renderer.minGridDistance = 100;
			categoryAxis.startLocation = GRID_DISTANCE[weekFilter]
				? GRID_DISTANCE[weekFilter][0]
				: 0;
			categoryAxis.endLocation = GRID_DISTANCE[weekFilter]
				? GRID_DISTANCE[weekFilter][1]
				: 1;

			series.name = name;
			series.strokeWidth = 2;
			series.zIndex = 10;
			series.tooltip.pointerOrientation = 'horizontal';
			series.tooltip.getFillFromObject = false;
			series.tooltip.background.fill = am4core.color(OFF_WHITE);

			if (color) {
				series.stroke = am4core.color(color);
			}

			if (dasharray) {
				series.strokeDasharray = dasharray;
			}

			const bullet = series.bullets.push(new am4core.Circle());
			bullet.radius = 1;
			bullet.focusable = true;
			bullet.fillOpacity = 0;
			bullet.strokeWidth = 0;
			bullet.readerTitle = '{categoryX} {valueY}';

			return series;
		}

		chartValue?.forEach((item) => {
			createLineSeries(item.value, item.name, item.color, item.chartLine);
		});

		// setting legends
		lineChart.legend = new am4charts.Legend();
		// lineChart.legend.disabled = isWOS;
		lineChart.legend.focusable = false;
		lineChart.legend.labels.template.fontSize = 12;
		lineChart.legend.labels.template.fill = am4core.color(FONT_COLOR);
		const markerTemplate = lineChart.legend.markers.template;
		markerTemplate.width = 24;
		markerTemplate.height = 8;
		// setting cursor
		lineChart.cursor = new am4charts.XYCursor();
		lineChart.cursor.lineY.disabled = true;
		lineChart.legend.reverseOrder = true;
		lineChart.focusable = true;
		// Disable toggle on legends
		lineChart.legend.itemContainers.template.clickable = false;
		lineChart.legend.itemContainers.template.focusable = false;
		lineChart.legend.itemContainers.template.cursorOverStyle =
			am4core.MouseCursorStyle.default;
		lineChart.zoomOutButton.disabled = true;

		chartRef.current = lineChart;
		return () => {
			lineChart.dispose();
		};
	}, [chartData, chartValue]);

	return (
		<>
			<Box id={chartName} className={classes.ComparisonChart}></Box>
		</>
	);
};

ComparisonChart.defaultProps = {
	chartData: [],
	chartHeight: '426 px',
	chartWidth: '850 px',
	chartValue: [],
	chartName: 'LineChart',
	xAxisData: {},
	weekFilter: 4,
};

ComparisonChart.propTypes = {
	chartData: PropTypes.arrayOf(PropTypes.shape({})),
	chartHeight: PropTypes.string,
	chartWidth: PropTypes.string,
	chartValue: PropTypes.arrayOf(PropTypes.shape({})),
	chartName: PropTypes.string,
	weekFilter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	xAxisData: PropTypes.shape({
		nonDate: PropTypes.string,
	}),
	yAxisMinValue: PropTypes.number,
};

export default ComparisonChart;
